import type { HTMLProps, ReactNode } from "react";
import type { FormProps as ReactAriaFormProps } from "react-aria-components";
import { Form as ReactAriaForm } from "react-aria-components";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormProvider, useFormContext } from "react-hook-form";

import { cn } from "../utils";
import type { NcButtonProps } from "./nc-button";
import { NcButton } from "./nc-button";
import type { NcGroupProps } from "./nc-group";
import { NcGroup } from "./nc-group";

export interface NcFormProps<T extends FieldValues> extends ReactAriaFormProps {
  form: UseFormReturn<T, unknown>;
  children: ReactNode;
}

export function NcForm<T extends FieldValues>({
  form,
  autoComplete = "off",
  onSubmit = e => e.preventDefault(),
  className,
  ...props
}: NcFormProps<T>) {
  return (
    <FormProvider data-nc="NcForm" {...form}>
      <ReactAriaForm
        className={cn("grid w-full gap-6", className)}
        autoComplete={autoComplete}
        onSubmit={onSubmit}
        {...props}
      />
    </FormProvider>
  );
}

type NcFormActionProps = HTMLProps<HTMLDivElement>;

const Actions = ({ className, ...props }: NcFormActionProps) => {
  return (
    <div
      data-nc="NcForm.Actions"
      className={cn("flex w-full flex-wrap gap-2.5", className)}
      {...props}
    />
  );
};
NcForm.Actions = Actions;

const ResetButton = ({ ...props }: NcButtonProps) => {
  const { reset } = useFormContext();

  const handleReset = () => {
    reset();
  };

  return <NcButton data-nc="NcForm.ResetButton" type="reset" onPress={handleReset} {...props} />;
};
NcForm.ResetButton = ResetButton;

const SubmitButton = ({ ...props }: NcButtonProps) => (
  <NcButton nc-data="NcForm.SubmitButton" type="submit" variant="primary" {...props} />
);
NcForm.SubmitButton = SubmitButton;

interface NcFormRowProps extends NcGroupProps {
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const FormRow = ({ className, ...props }: NcFormRowProps) => {
  return (
    <NcGroup
      data-nc="NcForm.FormRow"
      className={cn("flex flex-wrap gap-x-8 gap-y-4", className)}
      {...props}
    />
  );
};
NcForm.Row = FormRow;
